// src/api/adminOrders.ts
import { V1, authFetch } from "api/client";

export type OrderStatus =
  | "pending"
  | "paid"
  | "booked"
  | "collected"
  | "processing"
  | "resulted"
  | "refunded"
  | "cancelled";

export type LocationType = "clinic" | "mobile" | null;

/**
 * Admin order response (matches the flat admin API shape you showed).
 * Keep optional fields permissive for backward compatibility.
 */
export interface AdminOrderOut {
  id: number;
  user_id: number;

  user_email?: string | null;
  user_display_name?: string | null;
  user_label?: string | null;

  package_id?: number | null;
  package_name?: string | null;

  clinic_id?: number | null;
  clinic_name?: string | null;

  status: OrderStatus;

  subtotal_cents?: number | null;
  surcharge_cents?: number | null;
  tax_cents?: number | null;
  fees_cents?: number | null;
  total_cents?: number | null;
  currency: string;

  stripe_payment_intent_id?: string | null;

  mobile_address_line1?: string | null;
  mobile_address_line2?: string | null;
  mobile_city?: string | null;
  mobile_postcode?: string | null;

  note?: string | null;

  appointment_at?: string | null; // ISO string
  location_type?: LocationType;

  created_date: string;
  last_updated_date: string;
  lock_version: number;
}

export interface AdminListOrdersOut {
  items: AdminOrderOut[];
  page: number;
  page_size: number;
  total: number;
}

/**
 * Patch payload used by StatusUpdateForm (include note for read/write).
 */
export interface AdminPatchStatusIn {
  expected_lock_version: number;

  status?: OrderStatus;

  appointment_at?: string | null;
  location_type?: LocationType;

  mobile_address_line1?: string | null;
  mobile_address_line2?: string | null;
  mobile_city?: string | null;
  mobile_postcode?: string | null;

  note?: string | null;
}

function normalizeOrdersList(res: any, page: number, page_size: number): AdminListOrdersOut {
  if (Array.isArray(res)) {
    return { items: res as AdminOrderOut[], page, page_size, total: res.length };
  }
  if (Array.isArray(res?.items)) {
    return {
      items: res.items as AdminOrderOut[],
      page: res.page ?? page,
      page_size: res.page_size ?? page_size,
      total: res.total ?? res.items.length,
    };
  }
  if (Array.isArray(res?.orders)) {
    return {
      items: res.orders as AdminOrderOut[],
      page: res.page ?? page,
      page_size: res.page_size ?? page_size,
      total: res.total ?? res.orders.length,
    };
  }
  return { items: [], page, page_size, total: 0 };
}

/** GET /v1/admin/orders */
export async function adminListOrders(page = 1, pageSize = 20): Promise<AdminListOrdersOut> {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await authFetch<any>(`${V1}/admin/orders?${params.toString()}`, { method: "GET" });
  return normalizeOrdersList(res, page, pageSize);
}

/** GET /v1/admin/orders/:id */
export async function adminGetOrder(orderId: number): Promise<AdminOrderOut> {
  return authFetch<AdminOrderOut>(`${V1}/admin/orders/${orderId}`, {
    method: "GET",
  });
}

/** PATCH /v1/admin/orders/:id */
export async function patchStatus(
  orderId: number,
  payload: AdminPatchStatusIn
): Promise<AdminOrderOut> {
  return authFetch<AdminOrderOut>(`${V1}/admin/orders/${orderId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}
